import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import { thunks as PlaylistThunks } from 'reducers/PlayList'
import TransparentCoverIcon from 'images/icons/transparent_cover.svg'
import PlaceholderLogo from 'images/icons/placeholder.png'
import { whiteStarLogo, yellowStarLogo, PlayBigWhiteIcon } from 'images'
import {
  ImageWithFallback,
  AspectRatioContainer,
  AnimatedImage,
  PlayPlaylistModal
} from 'components'
import { theme } from '../../../tailwind.theme'

const PlaylistCell = ({
  playlist,
  toggleStarClick,
  onPlayClick,
  t,
  dispatch,
  isIpadView,
  selectedPlaylist,
  setSelectedPlaylist,
  hasPlayQueue
}) => {
  const [showModal, setShowModal] = useState(false)
  const navigate = useNavigate()

  const handleNavigate = () => {
    const hovered =
      selectedPlaylist && selectedPlaylist.playlistid === playlist.playlistid

    if ((isIpadView && hovered) || !isIpadView) {
      navigate(`/playlists/${playlist.playlistid}`)
    }
  }

  const handlePlay = () => {
    if (hasPlayQueue) {
      setShowModal(true)
    } else {
      onPlayClick(playlist)
    }

    if (setSelectedPlaylist) {
      setSelectedPlaylist(null)
    }
  }

  const handleSave = () => {
    toggleStarClick(playlist)
  }

  const handleClickYes = () => {
    setShowModal(false)
    onPlayClick(playlist)
  }

  const handleClickCancel = () => {
    setShowModal(false)
  }

  const handleDelete = () => {
    dispatch(PlaylistThunks.deletePlaylist(playlist))
  }

  const renderImage = (hovered) => {
    const { thumbnail, thumbnails } = playlist

    return (
      <>
        <ImageWithFallback
          className={`w-full h-full object-cover absolute top-0 left-0 slowTransition
            ${hovered && thumbnails.length > 1 ? 'opacity-0' : 'opacity-100'}`}
          src={thumbnail}
          fallback={PlaceholderLogo}
          alt="album cover"
        />
        <AnimatedImage
          className={`slowTransition ${
            hovered && thumbnails.length > 1 ? 'opacity-100' : 'opacity-0'
          }`}
          images={thumbnails}
        />
      </>
    )
  }

  const renderTransparentCover = () => (
    <div className="absolute top-0 left-0 w-full h-full">
      <img src={TransparentCoverIcon} className="w-full h-full" />
    </div>
  )

  const renderHoveredButtons = () => {
    const { tracks } = playlist
    const hasSongs = tracks && tracks > 0
    const {
      extend: {
        colors: { white, disabledGray }
      }
    } = theme

    return (
      <div className="absolute top-0 left-0 w-full h-full flex justify-around items-center">
        <button
          type="button"
          className="focus:outline-none z-15"
          style={{ color: hasSongs ? white : disabledGray }}
          onClick={(e) => {
            e.stopPropagation()
            if (hasSongs) {
              handlePlay()
            }
          }}
        >
          <PlayBigWhiteIcon width={90} height={90} viewBox="0 0 115 115" />
        </button>
      </div>
    )
  }

  const renderInfo = (playlist, hovered) => {
    const { name, tracks, pinned } = playlist

    return (
      <div className="absolute top-0 left-0 w-full h-full z-10">
        <div className="w-full px-4 py-1 mt-4 flex flex-row justify-between items-center bg-darkBlueGrey opacity-90 shadow-xl">
          <div className="text-white text-lg break-words-everywhere">
            {name}
          </div>
          {hovered || pinned ? (
            <button
              type="button"
              className="focus:outline-none flex-none"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
            >
              <img
                src={pinned ? yellowStarLogo : whiteStarLogo}
                className="ml-1 w-5 h-5"
                alt="star playlist button"
              />
            </button>
          ) : (
            <div />
          )}
        </div>
        <div className="mt-4 px-4 py-1 bg-darkBlueGrey inline-block opacity-90 shadow-lg">
          <div className="text-white text-xs">{`${tracks} ${t(
            'playlist.songs'
          )}`}</div>
        </div>
      </div>
    )
  }

  const renderContent = (hovered) => {
    return (
      <div className="flex px-4 py-4">
        <div
          className="pb-3 bg-darkBlueGrey relative w-full shadow-lg z-5"
          onClick={handleNavigate}
        >
          <AspectRatioContainer
            widthClassName="w-full"
            aspectClassName="aspect-ratio-16/9"
          >
            {renderImage(hovered)}
          </AspectRatioContainer>
          {renderInfo(playlist, hovered)}
          {hovered && renderHoveredButtons()}
        </div>
        {showModal && (
          <PlayPlaylistModal
            playlist={playlist}
            onClickYes={handleClickYes}
            onClickCancel={handleClickCancel}
          />
        )}
      </div>
    )
  }

  const hovered =
    selectedPlaylist && selectedPlaylist.playlistid === playlist.playlistid

  return (
    <div
      className="relative w-full cursor-pointer"
      onClick={(e) => {
        e.stopPropagation()
        if (isIpadView && setSelectedPlaylist) {
          if (hovered) {
            setSelectedPlaylist(null)
          } else {
            setSelectedPlaylist(playlist)
          }
        }
      }}
      onMouseEnter={() => {
        if (setSelectedPlaylist) {
          setSelectedPlaylist(playlist)
        }
      }}
      onMouseLeave={() => {
        if (setSelectedPlaylist) {
          setSelectedPlaylist(null)
        }
      }}
    >
      {renderContent(hovered)}
    </div>
  )
}

PlaylistCell.propTypes = {
  playlist: PropTypes.object.isRequired,
  toggleStarClick: PropTypes.func,
  onPlayClick: PropTypes.func,
  t: PropTypes.func,
  dispatch: PropTypes.func,
  isIpadView: PropTypes.bool,
  selectedPlaylist: PropTypes.object,
  setSelectedPlaylist: PropTypes.func,
  hasPlayQueue: PropTypes.bool
}

export default withTranslation()(connect()(PlaylistCell))
